import React from 'react'
import {
	Box,
	CircularProgress,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Tabs,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
} from '@mui/material'
import { useApolloClient } from '@apollo/client'

import ContentWrapper from '../components/ContentWrapper'
import Dialog from '../components/Dialog'
import IconButton from '../components/IconButton'
import Txt from '../components/Txt'
import { formatDateTime } from '../components/RestaurantCommunicationLog'
import {
	ApiMonitoring,
	ApiMonitoringInterval,
	OperationLogSource,
	useApiMonitoring,
} from '../utils/apiMonitoring'

// Beschriftung der Balken je Ansicht und wie viele Balken zwischen zwei Achsenbeschriftungen liegen.
const INTERVAL_FORMATS: Record<
	ApiMonitoringInterval,
	{ label: string; axisEvery: number; timeSlotLabel: Intl.DateTimeFormatOptions }
> = {
	DAY: { label: 'Tag', axisEvery: 3, timeSlotLabel: { hour: '2-digit', minute: '2-digit' } },
	WEEK: {
		label: 'Woche',
		axisEvery: 4,
		timeSlotLabel: { weekday: 'short', hour: '2-digit', minute: '2-digit' },
	},
	MONTH: { label: '30 Tage', axisEvery: 5, timeSlotLabel: { day: 'numeric', month: 'numeric' } },
}

const INTERVALS = Object.keys(INTERVAL_FORMATS) as ApiMonitoringInterval[]

const SOURCE_LABELS: Record<OperationLogSource, string> = {
	query: 'Query',
	mutation: 'Mutation',
	field: 'Feld',
	request: 'Anfrage',
	route: 'Route',
	job: 'Job',
	internal: 'Intern',
}

// Unerwartete Fehler (kein throwLocalError & Co.) — die API speichert dazu den Stacktrace.
const BUG_ERROR_CODE = 'INTERNAL_SERVER_ERROR'

const CHART_HEIGHT = '10rem'

const ROWS_PER_PAGE = 10

// Leere Auswahl blendet den „Zeilen pro Seite“-Wähler aus.
const ROWS_PER_PAGE_OPTIONS: number[] = []

const labelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) =>
	`${from}–${to} von ${count}`

const sxCard = {
	bgcolor: 'background.paper',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
	p: '1rem',
	minWidth: 0,
} as const

const sxToolbar = {
	display: 'flex',
	flexWrap: 'wrap',
	alignItems: 'center',
	gap: '0.75rem',
	width: '100%',
	m: '0 0 1rem 0',
} as const

const sxTiles = {
	display: 'grid',
	gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))',
	gap: '1rem',
	width: '100%',
	m: '0 0 1rem 0',
} as const

const sxSections = {
	display: 'grid',
	gridTemplateColumns: '1fr',
	gap: '1rem',
	width: '100%',
} as const

const sxTabs = { borderBottom: '1px solid', borderColor: 'divider', m: '0 0 0.5rem 0' } as const

const sxChart = {
	display: 'flex',
	alignItems: 'flex-end',
	gap: '2px',
	height: CHART_HEIGHT,
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

const sxBarColumn = {
	'flex': '1',
	'height': '100%',
	'display': 'flex',
	'flexDirection': 'column',
	'justifyContent': 'flex-end',
	'gap': '2px',
	'borderRadius': '4px 4px 0 0',
	'&:hover': { bgcolor: 'action.hover' },
	'& > :first-of-type': { borderRadius: '4px 4px 0 0' },
} as const

const sxAxis = { display: 'flex', gap: '2px', m: '0.25rem 0 0 0' } as const

// Je Balken ein gleich breiter Platz; die Beschriftung darf über ihn hinausragen.
const sxAxisSlot = { flex: '1', minWidth: 0, overflow: 'visible' } as const

const sxAxisLabel = { whiteSpace: 'nowrap' } as const

const sxLegend = { display: 'flex', gap: '1rem', alignItems: 'center' } as const

const sxSwatchSuccess = { width: '0.75rem', height: '0.75rem', borderRadius: '2px', bgcolor: 'primary.main' } as const

const sxSwatchError = { width: '0.75rem', height: '0.75rem', borderRadius: '2px', bgcolor: 'error.main' } as const

const sxSwatchRateLimited = {
	width: '0.75rem',
	height: '0.75rem',
	borderRadius: '2px',
	bgcolor: 'warning.main',
} as const

const sxClickableRow = { cursor: 'pointer' } as const

const sxShareCell = { width: '30%', minWidth: '6rem' } as const

const sxMessageCell = { maxWidth: '24rem' } as const

const sxShareBar = {
	'display': 'flex',
	'gap': '2px',
	'height': '0.6rem',
	'& > :first-of-type': { borderRadius: '2px 0 0 2px' },
	'& > :last-of-type': { borderRadius: '0 4px 4px 0' },
	'& > :only-of-type': { borderRadius: '2px 4px 4px 2px' },
} as const

const sxCode = {
	fontFamily: 'monospace',
	fontSize: '0.8rem',
	whiteSpace: 'pre-wrap',
	wordBreak: 'break-all',
	bgcolor: 'action.hover',
	p: '0.75rem',
	borderRadius: '0.25rem',
	m: '0 0 0.75rem 0',
} as const

const sxCardHead = {
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'space-between',
	alignItems: 'center',
	gap: '0.5rem 1rem',
	m: '0 0 0.5rem 0',
} as const

type Entry = ApiMonitoring['entries'][number]
type Operation = ApiMonitoring['operations'][number]
type TimeSlot = ApiMonitoring['timeline'][number]

const StatTile = React.memo(
	({ label, value, hint }: { label: string; value: string; hint?: string }): JSX.Element => (
		<Box sx={sxCard}>
			<Txt variant="body2" color="text.secondary">
				{label}
			</Txt>
			<Txt variant="h5" fontWeight="bold">
				{value}
			</Txt>
			{hint ? (
				<Txt variant="caption" color="text.secondary">
					{hint}
				</Txt>
			) : null}
		</Box>
	)
)
StatTile.displayName = 'StatTile'

// Ein Teil eines gestapelten Balkens; die Rundung setzt der umgebende Balken.
const BarSegment = React.memo(
	({ share, color, axis }: { share: number; color: string; axis: 'height' | 'width' }): JSX.Element => {
		const sx = React.useMemo(() => ({ [axis]: `${share * 100}%`, bgcolor: color }), [axis, color, share])
		return <Box sx={sx} />
	}
)
BarSegment.displayName = 'BarSegment'

// Ein Balken: oben Sperren, darunter Fehler, unten erfolgreiche Aufrufe, 2px Abstand dazwischen.
const TimelineBar = React.memo(
	({
		timeSlot,
		maxCount,
		intervalType,
	}: {
		timeSlot: TimeSlot
		maxCount: number
		intervalType: ApiMonitoringInterval
	}): JSX.Element => {
		const title = `${new Date(timeSlot.startsAt).toLocaleString(
			'de-DE',
			INTERVAL_FORMATS[intervalType].timeSlotLabel
		)}: ${timeSlot.count} Aufrufe, ${timeSlot.errorCount} Fehler, ${timeSlot.rateLimitedCount} Sperren`

		return (
			<Tooltip title={title} placement="top">
				<Box sx={sxBarColumn}>
					{timeSlot.rateLimitedCount ? (
						<BarSegment share={timeSlot.rateLimitedCount / maxCount} color="warning.main" axis="height" />
					) : null}
					{timeSlot.errorCount ? (
						<BarSegment share={timeSlot.errorCount / maxCount} color="error.main" axis="height" />
					) : null}
					{timeSlot.count > timeSlot.errorCount ? (
						<BarSegment
							share={(timeSlot.count - timeSlot.errorCount) / maxCount}
							color="primary.main"
							axis="height"
						/>
					) : null}
				</Box>
			</Tooltip>
		)
	}
)
TimelineBar.displayName = 'TimelineBar'

const Timeline = React.memo(
	({
		title,
		timeline,
		intervalType,
	}: {
		title: string
		timeline: TimeSlot[]
		intervalType: ApiMonitoringInterval
	}): JSX.Element => {
		const maxCount = Math.max(
			1,
			...timeline.map(({ count, rateLimitedCount }) => count + rateLimitedCount)
		)
		const { axisEvery, timeSlotLabel } = INTERVAL_FORMATS[intervalType]

		return (
			<Box sx={sxCard}>
				<Box sx={sxCardHead}>
					<Txt variant="subtitle2">{title}</Txt>
					<Box sx={sxLegend}>
						<Box sx={sxLegend}>
							<Box sx={sxSwatchSuccess} />
							<Txt variant="caption">Erfolgreich</Txt>
						</Box>
						<Box sx={sxLegend}>
							<Box sx={sxSwatchError} />
							<Txt variant="caption">Fehler</Txt>
						</Box>
						<Box sx={sxLegend}>
							<Box sx={sxSwatchRateLimited} />
							<Txt variant="caption">Rate-Limit-Sperre</Txt>
						</Box>
						<Txt variant="caption" color="text.secondary">
							max. {maxCount}
						</Txt>
					</Box>
				</Box>
				<Box sx={sxChart}>
					{timeline.map((timeSlot) => (
						<TimelineBar
							key={timeSlot.startsAt}
							timeSlot={timeSlot}
							maxCount={maxCount}
							intervalType={intervalType}
						/>
					))}
				</Box>
				<Box sx={sxAxis}>
					{timeline.map(({ startsAt }, index) => (
						<Box key={startsAt} sx={sxAxisSlot}>
							{index % axisEvery ? null : (
								<Txt variant="caption" color="text.secondary" sx={sxAxisLabel}>
									{new Date(startsAt).toLocaleString('de-DE', timeSlotLabel)}
								</Txt>
							)}
						</Box>
					))}
				</Box>
			</Box>
		)
	}
)
Timeline.displayName = 'Timeline'

// Je Art ein Tab, in dieser Reihenfolge; Arten ohne Aufrufe im Zeitraum fehlen.
const SOURCE_SECTIONS: { source: OperationLogSource; label: string }[] = [
	{ source: 'mutation', label: 'Mutationen' },
	{ source: 'query', label: 'Queries' },
	{ source: 'job', label: 'Jobs' },
	{ source: 'field', label: 'Felder' },
	{ source: 'request', label: 'Ungültige Anfragen' },
	{ source: 'route', label: 'Routen' },
	{ source: 'internal', label: 'Intern' },
]

// Balkenlänge relativ zur häufigsten Operation der Art, Fehleranteil rot am Ende.
const OperationRow = React.memo(
	({
		operation,
		maxCount,
		totalCount,
		selected,
		onSelect,
	}: {
		operation: Operation
		maxCount: number
		totalCount: number
		selected: boolean
		onSelect: (operationName: string) => void
	}): JSX.Element => {
		const handleClick = React.useCallback(
			() => onSelect(operation.operationName),
			[operation.operationName, onSelect]
		)

		return (
			<TableRow hover selected={selected} onClick={handleClick} sx={sxClickableRow}>
				<TableCell>{operation.operationName}</TableCell>
				<TableCell sx={sxShareCell}>
					<Tooltip
						title={`${Math.round((operation.count / totalCount) * 100)} % der Aufrufe dieser Art`}
						placement="top"
					>
						<Box sx={sxShareBar}>
							{operation.count > operation.errorCount ? (
								<BarSegment
									share={(operation.count - operation.errorCount) / maxCount}
									color="primary.main"
									axis="width"
								/>
							) : null}
							{operation.errorCount ? (
								<BarSegment share={operation.errorCount / maxCount} color="error.main" axis="width" />
							) : null}
						</Box>
					</Tooltip>
				</TableCell>
				<TableCell align="right">{operation.count}</TableCell>
				<TableCell align="right">
					{operation.errorCount ? (
						<Txt variant="body2" color="error.main">
							{operation.errorCount} ({Math.round((operation.errorCount / operation.count) * 100)} %)
						</Txt>
					) : (
						'0'
					)}
				</TableCell>
				<TableCell align="right">
					{operation.rateLimitedCount ? (
						<Txt variant="body2" color="warning.main" fontWeight="bold">
							{operation.rateLimitedCount}
						</Txt>
					) : (
						'0'
					)}
				</TableCell>
				<TableCell align="right">{operation.userCount}</TableCell>
				<TableCell align="right">
					{operation.p95DurationMs === null ? '–' : `${operation.p95DurationMs} ms`}
				</TableCell>
				<TableCell>{formatDateTime(operation.lastCalledAt)}</TableCell>
			</TableRow>
		)
	}
)
OperationRow.displayName = 'OperationRow'

// Die API liefert die Operationen absteigend nach Aufrufen; die erste gibt die volle Balkenlänge vor.
const OperationTable = React.memo(
	({
		operations,
		totalCount,
		selectedOperationName,
		onSelect,
	}: {
		operations: Operation[]
		totalCount: number
		selectedOperationName: string | null
		onSelect: (operationName: string) => void
	}): JSX.Element => {
		const [page, setPage] = React.useState(0)
		const handleChangePage = React.useCallback((_: unknown, nextPage: number) => setPage(nextPage), [])
		// Nach einem Zeitraumwechsel kann die Seite hinter dem Ende liegen.
		const currentPage = Math.min(page, Math.max(0, Math.ceil(operations.length / ROWS_PER_PAGE) - 1))

		return (
			<>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Operation</TableCell>
							<TableCell>Häufigkeit</TableCell>
							<TableCell align="right">Aufrufe</TableCell>
							<TableCell align="right">Fehler</TableCell>
							<TableCell align="right">Sperren</TableCell>
							<TableCell align="right">Nutzer</TableCell>
							<TableCell align="right">p95</TableCell>
							<TableCell>Zuletzt</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{operations
							.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE)
							.map((operation) => (
								<OperationRow
									key={operation.operationName}
									operation={operation}
									maxCount={Math.max(1, operations[0].count)}
									totalCount={Math.max(1, totalCount)}
									selected={operation.operationName === selectedOperationName}
									onSelect={onSelect}
								/>
							))}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={operations.length}
					page={currentPage}
					onPageChange={handleChangePage}
					rowsPerPage={ROWS_PER_PAGE}
					rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
					labelDisplayedRows={labelDisplayedRows}
				/>
			</>
		)
	}
)
OperationTable.displayName = 'OperationTable'

const EntryRow = React.memo(({ entry }: { entry: Entry }): JSX.Element => {
	const resultColor = entry.status === 'RATE_LIMITED' ? 'warning.main' : 'error.main'
	const resultLabel =
		entry.status === 'RATE_LIMITED'
			? 'Rate-Limit erreicht'
			: `${entry.errorCode ?? 'Fehler'}: ${entry.errorMessage ?? '–'}`
	const user = !entry.userId
		? '–'
		: entry.isRoot
		? `Root #${entry.userId}`
		: entry.isAnonymous
		? `anonym #${entry.userId}`
		: `#${entry.userId}`

	const handleClick = React.useCallback(
		() =>
			Dialog.render({
				title: `${entry.operationName} · ${formatDateTime(entry.datetime)}`,
				renderDescription: () => (
					<>
						<Txt variant="body2" m="0 0 0.5rem 0">
							{SOURCE_LABELS[entry.source]} · {user} · {entry.ip ?? 'keine IP'}
							{entry.durationMs === null ? '' : ` · ${entry.durationMs} ms`}
						</Txt>
						<Txt variant="body2" color={resultColor} m="0 0 0.75rem 0">
							{resultLabel}
						</Txt>
						{entry.errorStack ? (
							<Box component="pre" sx={sxCode}>
								{entry.errorStack}
							</Box>
						) : null}
						<Box component="pre" sx={sxCode}>
							{JSON.stringify(JSON.parse(entry.params), null, 2)}
						</Box>
					</>
				),
			}),
		[entry, resultColor, resultLabel, user]
	)

	return (
		<TableRow hover onClick={handleClick} sx={sxClickableRow}>
			<TableCell>{formatDateTime(entry.datetime)}</TableCell>
			<TableCell>{entry.operationName}</TableCell>
			<TableCell>{user}</TableCell>
			<TableCell>{entry.ip ?? '–'}</TableCell>
			<TableCell align="right">{entry.durationMs === null ? '–' : `${entry.durationMs} ms`}</TableCell>
			<TableCell sx={sxMessageCell}>
				<Txt
					variant="body2"
					color={resultColor}
					fontWeight={entry.errorCode === BUG_ERROR_CODE ? 'bold' : undefined}
					numberOfLines={1}
					noUnderLine
				>
					{resultLabel}
				</Txt>
			</TableCell>
		</TableRow>
	)
})
EntryRow.displayName = 'EntryRow'

const ErrorList = React.memo(({ title, entries }: { title: string; entries: Entry[] }): JSX.Element => {
	const [page, setPage] = React.useState(0)
	const handleChangePage = React.useCallback((_: unknown, nextPage: number) => setPage(nextPage), [])
	// Nach einem Zeitraumwechsel kann die Seite hinter dem Ende liegen.
	const currentPage = Math.min(page, Math.max(0, Math.ceil(entries.length / ROWS_PER_PAGE) - 1))

	return (
		<Box sx={sxCard}>
			<Box sx={sxCardHead}>
				<Txt variant="subtitle2">{title}</Txt>
				<Txt variant="caption" color="text.secondary">
					Klick zeigt Parameter und Stacktrace
				</Txt>
			</Box>
			{entries.length ? (
				<>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Zeit</TableCell>
								<TableCell>Operation</TableCell>
								<TableCell>Nutzer</TableCell>
								<TableCell>IP</TableCell>
								<TableCell align="right">Dauer</TableCell>
								<TableCell>Ergebnis</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{entries
								.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE)
								.map((entry, index) => (
									<EntryRow key={`${entry.datetime}-${entry.operationName}-${index}`} entry={entry} />
								))}
						</TableBody>
					</Table>
					<TablePagination
						component="div"
						count={entries.length}
						page={currentPage}
						onPageChange={handleChangePage}
						rowsPerPage={ROWS_PER_PAGE}
						rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
						labelDisplayedRows={labelDisplayedRows}
					/>
				</>
			) : (
				<Txt variant="body2" color="text.secondary">
					Keine Fehler im Zeitraum.
				</Txt>
			)}
		</Box>
	)
})
ErrorList.displayName = 'ErrorList'

// Eigene Abfrage nur für die gewählte Operation: zuerst ihre Fehler, darunter ihr Verlauf.
const OperationDetail = React.memo(
	({
		intervalType,
		offset,
		operationName,
	}: {
		intervalType: ApiMonitoringInterval
		offset: number
		operationName: string
	}): JSX.Element => {
		const { monitoring } = useApiMonitoring(intervalType, offset, operationName)

		return !monitoring ? (
			<CircularProgress />
		) : (
			<>
				<ErrorList title={`Fehler · ${operationName}`} entries={monitoring.entries} />
				<Timeline
					title={`Aufrufe im Verlauf · ${operationName}`}
					timeline={monitoring.timeline}
					intervalType={intervalType}
				/>
			</>
		)
	}
)
OperationDetail.displayName = 'OperationDetail'

const ApiMonitoringView = (): JSX.Element => {
	const [intervalType, setIntervalType] = React.useState<ApiMonitoringInterval>('DAY')
	const [offset, setOffset] = React.useState(0)
	const [source, setSource] = React.useState<OperationLogSource | null>(null)
	const [operationName, setOperationName] = React.useState<string | null>(null)
	const { monitoring } = useApiMonitoring(intervalType, offset, null)
	const client = useApolloClient()

	const handleChangeInterval = React.useCallback(
		(_: React.MouseEvent<HTMLElement>, nextIntervalType: ApiMonitoringInterval | null) => {
			if (nextIntervalType) {
				setIntervalType(nextIntervalType)
				setOffset(0)
			}
		},
		[]
	)
	const handleOlder = React.useCallback(() => setOffset((previous) => previous + 1), [])
	const handleNewer = React.useCallback(() => setOffset((previous) => previous - 1), [])
	// Lädt die Übersicht und, falls offen, die Detailansicht der gewählten Operation neu.
	const handleRefetch = React.useCallback(
		() => client.refetchQueries({ include: ['ApiMonitoring'] }),
		[client]
	)
	const handleSelectOperation = React.useCallback(
		(nextOperationName: string) =>
			setOperationName((previous) => (previous === nextOperationName ? null : nextOperationName)),
		[]
	)
	const handleChangeSource = React.useCallback(
		(_: React.SyntheticEvent, nextSource: OperationLogSource) => {
			setSource(nextSource)
			setOperationName(null)
		},
		[]
	)

	const operationSections = React.useMemo(
		() =>
			SOURCE_SECTIONS.map(({ source, label }) => ({
				source,
				label,
				operations: monitoring?.operations.filter((operation) => operation.source === source) ?? [],
			}))
				.filter(({ operations }) => operations.length)
				.map((section) => ({
					...section,
					totalCount: section.operations.reduce((sum, { count }) => sum + count, 0),
				})),
		[monitoring?.operations]
	)

	// Verschwindet die gewählte Art im neuen Zeitraum, springt der Tab auf die erste vorhandene.
	const activeSection =
		operationSections.find((section) => section.source === source) ?? operationSections[0]

	return (
		<ContentWrapper p="1rem">
			<Box sx={sxToolbar}>
				<Txt variant="h6" fontWeight="bold">
					API-Nutzung
				</Txt>
				<ToggleButtonGroup size="small" exclusive value={intervalType} onChange={handleChangeInterval}>
					{INTERVALS.map((interval) => (
						<ToggleButton key={interval} value={interval}>
							{INTERVAL_FORMATS[interval].label}
						</ToggleButton>
					))}
				</ToggleButtonGroup>
				<IconButton iconName="ChevronLeft" toolTip="Früher" onClick={handleOlder} />
				<IconButton
					iconName="ChevronRight"
					toolTip="Später"
					disabled={offset === 0}
					onClick={handleNewer}
				/>
				{monitoring ? (
					<Txt variant="body2" color="text.secondary">
						{formatDateTime(monitoring.from)} – {formatDateTime(monitoring.to)}
					</Txt>
				) : null}
				<IconButton iconName="Replay" toolTip="Neu laden" onClick={handleRefetch} />
			</Box>
			{!monitoring ? (
				<CircularProgress />
			) : (
				<>
					<Box sx={sxTiles}>
						<StatTile
							label="Aufrufe"
							value={String(monitoring.totalCount)}
							hint="Mutationen und Jobs, Queries nur mit Fehler"
						/>
						<StatTile
							label="Fehler"
							value={String(monitoring.errorCount)}
							hint={
								monitoring.totalCount
									? `${Math.round((monitoring.errorCount / monitoring.totalCount) * 100)} % der Aufrufe`
									: undefined
							}
						/>
						<StatTile
							label="Bugs"
							value={String(monitoring.bugCount)}
							hint="unerwartete Fehler mit Stacktrace"
						/>
						<StatTile
							label="Rate-Limit-Sperren"
							value={String(monitoring.rateLimitedCount)}
							hint="abgelehnte Aufrufe"
						/>
						<StatTile label="Nutzer" value={String(monitoring.userCount)} />
						<StatTile label="IP-Adressen" value={String(monitoring.ipCount)} />
						<StatTile
							label="E-Mails"
							value={String(monitoring.emailCount)}
							hint="erfolgreich versendet"
						/>
						<StatTile
							label="Neue Nutzer"
							value={String(monitoring.newRegisteredUserCount)}
							hint="mit E-Mail, ohne Testnutzer"
						/>
						<StatTile
							label="Neue anonyme Nutzer"
							value={String(monitoring.newAnonymousUserCount)}
							hint="noch ohne E-Mail"
						/>
						<StatTile
							label="Guthaben verteilt"
							value={(monitoring.bonusGrantedInCents / 100).toLocaleString('de-DE', {
								style: 'currency',
								currency: 'EUR',
							})}
							hint="Einladungs- und Motivationsbonus"
						/>
					</Box>
					<Box sx={sxSections}>
						<ErrorList title="Fehler" entries={monitoring.entries} />
						{activeSection ? (
							<Box sx={sxCard}>
								<Tabs
									value={activeSection.source}
									onChange={handleChangeSource}
									variant="scrollable"
									sx={sxTabs}
								>
									{operationSections.map(({ source, label, totalCount }) => (
										<Tab key={source} value={source} label={`${label} · ${totalCount}`} />
									))}
								</Tabs>
								<OperationTable
									key={activeSection.source}
									operations={activeSection.operations}
									totalCount={activeSection.totalCount}
									selectedOperationName={operationName}
									onSelect={handleSelectOperation}
								/>
							</Box>
						) : null}
						{operationName ? (
							<OperationDetail
								intervalType={intervalType}
								offset={offset}
								operationName={operationName}
							/>
						) : null}
					</Box>
				</>
			)}
		</ContentWrapper>
	)
}

export default ApiMonitoringView
